import React from "react";
import ReactECharts from "echarts-for-react";
import { useCopyElementToClipboard } from "@src/hooks/use-copy-element-to-clipboard";
import { CopyElementToClipboard } from "@src/components/copy-element-to-clipboard";
interface RetentionMetric {
    [variantKey: string]: Record<string, any>;
}

interface BreakdownData {
    [retentionKey: string]: RetentionMetric;
}

interface SignificanceResult {
    [breakdownKey: string]: BreakdownData;
}

interface RetentionChartProps {
    metrics: string[];
    data: SignificanceResult;
    breakdown?: {
        values: string[];
    };
    variantsOption?: {
        label: string | undefined;
        value: string;
    }[];
    className?: string;
    style?: React.CSSProperties;
    chartHeight?: number;
    metricType: "retention" | "sum_retention";
}

// Color palette for variants (similar to the image)
const COLOR_LIST = [
    "#ff6b6b", // Red for Ab_exp_v1
    "#4ecdc4", // Teal for Ab_exp_v4
    "#45b7d1", // Blue for Ab_exp_v2
    "#96ceb4", // Green for Ab_exp_v3
    "#feca57", // Yellow
    "#ff9ff3", // Pink
    "#54a0ff", // Light blue
    "#5f27cd", // Purple
];

export const RetentionChart: React.FC<RetentionChartProps> = ({
    metrics,
    data,
    breakdown,
    variantsOption = [],
    className,
    style,
    chartHeight = 400,
    metricType,
}) => {
    // Container ref for responsive behavior
    const containerRef = React.useRef<HTMLDivElement>(null);
    const [containerWidth, setContainerWidth] = React.useState<number>(1200);
    const [parentWidth, setParentWidth] = React.useState<number>(1200);

    // Handle window resize for responsive behavior
    React.useEffect(() => {
        const updateContainerWidth = () => {
            if (containerRef.current) {
                const parent = containerRef.current.parentElement;
                if (parent) {
                    setParentWidth(parent.offsetWidth);
                }
                setContainerWidth(containerRef.current.offsetWidth);
            }
        };

        // Initial measurement
        updateContainerWidth();

        // Add resize listener
        window.addEventListener("resize", updateContainerWidth);

        // Cleanup
        return () => {
            window.removeEventListener("resize", updateContainerWidth);
        };
    }, []);

    // Get variant name from options or use the value directly
    const getVariantName = React.useCallback(
        (value: string) => {
            return variantsOption.find((opt) => opt.value === value)?.label || value;
        },
        [variantsOption],
    );

    // Get color for variant
    const getVariantColor = React.useCallback((variant: string, index: number) => {
        return COLOR_LIST[index % COLOR_LIST.length];
    }, []);

    // Calculate uplift percentage using formula: (mean_variant - mean_base) / mean_base
    const calculateUplift = React.useCallback((variantData: Record<string, any>): number | null => {
        if (variantData.mean_base === null || variantData.mean_variant === null || variantData.mean_base === 0) {
            return null;
        }
        return (variantData.mean_variant - variantData.mean_base) / variantData.mean_base;
    }, []);

    // Calculate minimum width based on retention count
    const calculateMinWidth = React.useCallback((retentionCount: number): number => {
        return retentionCount * 100; // 100px per retention day
    }, []);

    // Process data based on breakdown prop
    const processedData = React.useMemo(() => {
        // If breakdown is provided, use its values; otherwise use "-" for no breakdown
        const breakdowns: string[] = breakdown?.values && breakdown.values.length > 0 ? breakdown.values : ["-"];

        const dataToProcess = data;
        return breakdowns
            .map((breakdownKey) => {
                const breakdownData = dataToProcess[breakdownKey];
                if (!breakdownData) return null;
                // Get all retention keys (retention0, retention1, etc.) and sort them

                // Get all unique variants across all retentions
                const allVariants = new Set<string>();
                metrics.forEach((retentionKey) => {
                    if (breakdownData[retentionKey]) {
                        Object.keys(breakdownData[retentionKey]).forEach((variant) => {
                            // Skip base group variants (they have uplift = 0)
                            const variantData = breakdownData[retentionKey][variant];
                            if (variantData.variant_group !== variantData.base_group) {
                                allVariants.add(variant);
                            }
                        });
                    }
                });
                const variants = Array.from(allVariants);

                // Prepare data for each variant
                const seriesData = variants.map((variant, index) => {
                    const variantUpliftData = metrics.map((retentionKey) => {
                        const variantData = breakdownData[retentionKey]?.[variant];
                        if (!variantData) return 0;

                        const uplift = calculateUplift(variantData);
                        return uplift !== null ? uplift : 0;
                    });

                    return {
                        name: getVariantName(variant),
                        type: "bar" as const,
                        data: variantUpliftData,
                        itemStyle: {
                            color: getVariantColor(variant, index),
                            borderRadius: [2, 2, 0, 0] as [number, number, number, number],
                        },
                        emphasis: {
                            itemStyle: {
                                shadowBlur: 10,
                                shadowOffsetX: 0,
                                shadowColor: "rgba(0, 0, 0, 0.5)",
                            },
                        },
                    };
                });

                return {
                    breakdownKey,
                    metrics,
                    variants,
                    seriesData,
                    minWidth: calculateMinWidth(metrics.length),
                };
            })
            .filter(Boolean);
    }, [data, breakdown, calculateUplift, getVariantName, getVariantColor, calculateMinWidth, metrics]);

    const renderCharts = () => {
        return processedData.map((chartData, chartIndex) => {
            if (!chartData) return null;

            return (
                <div key={chartData.breakdownKey} className="mb-5">
                    <RetentionChartItem
                        metricType={metricType}
                        getVariantName={getVariantName}
                        containerWidth={containerWidth}
                        parentWidth={parentWidth}
                        chartData={chartData}
                        chartIndex={chartIndex}
                        chartHeight={chartHeight}
                    />
                </div>
            );
        });
    };

    return (
        <div
            ref={containerRef}
            className={className}
            style={{
                width: "100%",
                ...style,
            }}
        >
            {renderCharts()}
        </div>
    );
};

const RetentionChartItem: React.FC<{
    chartData: Record<string, any>;
    chartIndex: number;
    chartHeight: number;
    containerWidth: number;
    parentWidth: number;
    getVariantName: (variant: string) => string;
    metricType: "retention" | "sum_retention";
}> = ({ chartData, chartIndex, chartHeight, containerWidth, parentWidth, getVariantName, metricType }) => {
    const needsScroll = chartData.minWidth > parentWidth;
    const chartWidth = needsScroll ? chartData.minWidth : "100%";

    const option: echarts.EChartsOption = {
        title: {
            text:
                chartData.breakdownKey === "-"
                    ? `% Change ${metricType === "retention" ? "Retention" : "Sum Retention"} vs Baseline`
                    : `% Change ${metricType === "retention" ? "Retention" : "Sum Retention"} vs Baseline - ${
                          chartData.breakdownKey
                      }`,
            left: "center",
            textStyle: {
                fontSize: 14,
                fontWeight: "bold",
            },
        },
        tooltip: {
            trigger: "axis",
            axisPointer: {
                type: "shadow",
            },
            formatter: (params: any) => {
                let result = `${params[0].axisValue}<br/>`;
                params.forEach((param: any) => {
                    const value = param.value;
                    const percentage = `${(value * 100).toFixed(2)}%`;
                    const color = param.color;
                    const sign = value >= 0 ? "+" : "";
                    result += `<span style="display:inline-block;margin-right:5px;border-radius:10px;width:10px;height:10px;background-color:${color};"></span>`;
                    result += `${param.seriesName}: ${sign}${percentage}<br/>`;
                });
                return result;
            },
        },
        legend: {
            data: chartData.variants.map((variant: string) => getVariantName(variant)),
            top: 30,
            type: "scroll",
        },
        grid: {
            left: "3%",
            right: "4%",
            bottom: "15%",
            top: "25%",
            containLabel: true,
        },
        xAxis: {
            type: "category",
            data: chartData?.metrics?.map((key: string) =>
                key.replace("retention", "").replace("sum_retention_per_user", ""),
            ),
            name: "Retention Day",
            nameLocation: "middle",
            nameGap: 30,
        },
        yAxis: {
            type: "value",
            axisLabel: {
                formatter: (value: number) => {
                    const sign = value >= 0 ? "+" : "";
                    return `${sign}${(value * 100).toFixed(0)}%`;
                },
            },
            name: "% Change",
            nameLocation: "middle",
            nameGap: 50,
            splitLine: {
                lineStyle: {
                    type: "dashed",
                    opacity: 0.5,
                },
            },
            // Add a reference line at y=0
            axisLine: {
                lineStyle: {
                    color: "#666",
                },
            },
        },
        series: chartData.seriesData,
        animationDuration: 1000,
        animationEasing: "cubicOut",
    };

    const containerRef = React.useRef<HTMLDivElement>(null);
    const { handleCopyToClipboard } = useCopyElementToClipboard(containerRef);
    return (
        <div
            className="relative"
            ref={containerRef}
            style={{
                width: "100%",
                overflowX: needsScroll ? "auto" : "visible",
                minWidth: needsScroll ? `${chartData.minWidth}px` : "auto",
            }}
        >
            <div className="absolute top-1 right-6">
                <CopyElementToClipboard
                    onCopy={handleCopyToClipboard}
                    className={"text-blue-400 sticky top-0 right-0 float-right z-50 ml-auto w-fit"}
                />
            </div>
            <div className="clear-both"></div>
            <ReactECharts
                key={`${chartData.breakdownKey}-${chartIndex}`}
                option={option}
                style={{
                    height: `${chartHeight}px`,
                    width: chartWidth,
                    minWidth: needsScroll ? `${chartData.minWidth}px` : "auto",
                }}
                opts={{
                    renderer: "canvas",
                    width: needsScroll ? chartData.minWidth : containerWidth - 40,
                    height: chartHeight,
                }}
            />
        </div>
    );
};
